import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { usePersistedState } from '../hooks/usePersistedState';

export type LayoutVersion = 'v1' | 'v2';

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
  minimalTheme: boolean;
  toggleMinimalTheme: () => void;
  layoutVersion: LayoutVersion;
  setLayoutVersion: (v: LayoutVersion) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [darkMode, setDarkMode] = usePersistedState<boolean>('crm_dark_mode', true);
  const [minimalTheme, setMinimalTheme] = usePersistedState<boolean>('crm_minimal_theme', false);
  const [layoutVersion, setLayoutVersion] = usePersistedState<LayoutVersion>('crm_layout_version', 'v2', { version: 2 });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    // v2 sempre usa o palette minimal (neutral gray)
    const shouldBeMinimal = minimalTheme || layoutVersion === 'v2';
    if (shouldBeMinimal) {
      document.documentElement.classList.add('theme-minimal');
    } else {
      document.documentElement.classList.remove('theme-minimal');
    }
  }, [minimalTheme, layoutVersion]);

  const toggleDarkMode = () => setDarkMode(!darkMode);
  const toggleMinimalTheme = () => setMinimalTheme(!minimalTheme);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode, minimalTheme, toggleMinimalTheme, layoutVersion, setLayoutVersion }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
